import useModal from '@/hooks/useModal'
import { Capacity } from '@/services/capacity/types'
import { colors, colorSystem } from '@/styles/theme'
import { HStack, Icon } from '@musinsa/mwms-ds'
import styled, { css } from 'styled-components'
import { BaseCell } from '../shared/BaseCell'
import { SetDeltaModal } from './SetDeltaModal'
import Text from '@/components/content/Text'
import { useCutOffDate } from '../../hooks/useCutOffDate'
import dayjs from 'dayjs'
import { DATE_FORMAT } from '@/utils/consts'

type Props = {
  data: Capacity
  warehouseCode: string
  warehouseName: string
}

export function CapaCell({ data, warehouseCode, warehouseName }: Props) {
  // totalAllocation은 음수로 옴 (50할당시 -50로 옴)
  const { totalCapacity, totalAllocation, capacityType } = data

  const { cutOffDate } = useCutOffDate()
  const isCutOffDateToday = dayjs().format(DATE_FORMAT.date) === cutOffDate

  const setDeltaModal = useModal()

  const isOverCapacity = Math.abs(totalAllocation) >= totalCapacity && totalCapacity > 0
  const textColor = isOverCapacity ? colorSystem.text.common.error : colorSystem.text.common.primary
  const capacityPercent = isOverCapacity ? 100 : 100 - (Math.abs(totalAllocation) / totalCapacity) * 100

  return (
    <>
      <CapaCellWrap
        $clickable={isCutOffDateToday}
        onClick={() => {
          if (!isCutOffDateToday) return
          setDeltaModal.onOpenModal()
        }}
      >
        <PercentBackground $isOverCapacity={isOverCapacity} $percent={capacityPercent} />
        <HStack gap={4} align="center" style={{ zIndex: 1 }}>
          <Text
            kind="Body_12_regular"
            style={{
              textDecoration: 'underline',
              textDecorationSkipInk: 'none',
            }}
            color={textColor}
          >
            {totalCapacity?.toLocaleString()}
          </Text>
          <Icon className="edit-icon" name="edit" size={16} color={isOverCapacity ? 'red500' : 'gray800'} />
        </HStack>
        <HStack gap={4} align="center" style={{ zIndex: 1 }}>
          <Text kind="Body_12_regular" color={textColor}>
            잔여
          </Text>
          <Text kind="Body_14_medium" color={textColor}>
            {(totalCapacity + totalAllocation).toLocaleString()}
          </Text>
        </HStack>
      </CapaCellWrap>

      {setDeltaModal.open && (
        <SetDeltaModal
          capacityType={capacityType}
          warehouseCode={warehouseCode}
          warehouseName={warehouseName}
          totalCapacity={totalCapacity}
          onClose={setDeltaModal.onCloseModal}
        />
      )}
    </>
  )
}

const CapaCellWrap = styled(BaseCell)<{ $clickable: boolean }>`
  flex: 1;
  position: relative;
  justify-content: space-between;

  .edit-icon {
    display: none;
  }

  ${({ $clickable }) =>
    $clickable &&
    css`
      cursor: pointer;

      &:hover {
        .edit-icon {
          display: block;
        }
      }
    `}

  &:last-child {
    border-bottom: none;
  }
`

const PercentBackground = styled.div<{ $isOverCapacity: boolean; $percent: number }>`
  position: absolute;
  top: 0;
  left: 0;
  height: 100%;
  width: ${({ $percent }) => `${Math.min($percent, 100)}%`};
  background-color: ${({ $isOverCapacity }) => ($isOverCapacity ? colors.red100 : colors.blue100)};
  z-index: 0;
`
